import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Briefcase, Building, Clock, DollarSign, CheckCircle2, AlertCircle, Heart, ArrowRight } from 'lucide-react';
import { MOCK_JOBS } from '../data/mock';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { formatDate } from '../lib/utils';
import { JobBadges, JobSalaryExtras } from '../components/JobBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VagaDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [job, setJob] = useState(MOCK_JOBS.find((j) => j.id === id) || null);
  const [applied, setApplied] = useState(false);
  const [applying, setApplying] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${id}`);
        if (res.data) setJob(res.data);
      } catch (e) { /* keep mock */ }
      try {
        const sim = await axios.get(`${API}/jobs/${id}/similar`);
        setSimilar(sim.data || []);
      } catch (e) { /* ignore */ }
      if (user && token) {
        try {
          const res = await axios.get(`${API}/applications/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data?.some((a) => a.job_id === id)) setApplied(true);
        } catch (e) { /* ignore */ }
        if (user.role === 'candidate') {
          try {
            const fav = await axios.get(`${API}/favorites/ids`, { headers: { Authorization: `Bearer ${token}` } });
            setIsFav((fav.data || []).includes(id));
          } catch (e) { /* ignore */ }
        }
      }
    };
    load();
  }, [id, user, token]);

  const toggleFavorite = async () => {
    if (!user) {
      toast({ title: 'Faça login para favoritar', description: 'Acesse o portal do candidato para salvar vagas.' });
      navigate('/portal/candidato/login');
      return;
    }
    try {
      if (isFav) {
        await axios.delete(`${API}/favorites/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setIsFav(false);
        toast({ title: 'Removida dos favoritos' });
      } else {
        await axios.post(`${API}/favorites`, { job_id: id }, { headers: { Authorization: `Bearer ${token}` } });
        setIsFav(true);
        toast({ title: 'Vaga salva nos favoritos ❤' });
      }
    } catch (e) {
      toast({ title: 'Não foi possível atualizar', variant: 'destructive' });
    }
  };

  const applyToJob = async () => {
    if (!user) {
      toast({ title: 'Faça login para se candidatar', description: 'Acesse o portal do candidato para continuar.' });
      navigate('/portal/candidato/login');
      return;
    }
    setApplying(true);
    try {
      await axios.post(`${API}/applications`, { job_id: id }, { headers: { Authorization: `Bearer ${token}` } });
      setApplied(true);
      toast({ title: 'Candidatura enviada!', description: 'Acompanhe o status no seu painel.' });
    } catch (e) {
      toast({ title: 'Não foi possível enviar', description: e.response?.data?.detail || 'Tente novamente em instantes.', variant: 'destructive' });
    }
    setApplying(false);
  };

  if (!job) return (
    <div className="section-padding container-hm text-center">
      <AlertCircle className="w-12 h-12 text-slate-400 mx-auto" />
      <h2 className="mt-3 text-2xl font-bold">Vaga não encontrada</h2>
      <Link to="/para-candidatos" className="mt-4 inline-block text-hm-orange font-semibold">Voltar para vagas</Link>
    </div>
  );

  return (
    <div className="bg-slate-50">
      <section className="hm-gradient text-white py-14">
        <div className="container-hm px-6">
          <Link to="/para-candidatos" className="inline-flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium"><ArrowLeft className="w-4 h-4" /> Voltar para vagas</Link>
          <div className="mt-4 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/80 font-semibold">{job.area}</p>
              <h1 className="text-3xl md:text-5xl font-extrabold mt-1">{job.title}</h1>
              <div className="mt-3 flex flex-wrap gap-3 text-sm text-white/90">
                <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {job.location}</span>
                <span className="inline-flex items-center gap-1"><Briefcase className="w-4 h-4" /> {job.type}</span>
                <span className="inline-flex items-center gap-1"><Building className="w-4 h-4" /> {job.modality}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> Publicada em {formatDate(job.createdAt)}</span>
              </div>
              <JobBadges job={job} className="mt-4" />
            </div>
            <div className="text-right">
              <div className="text-xs text-white/80">Remuneração</div>
              <div className="text-2xl font-extrabold">{job.salary}</div>
              <JobSalaryExtras job={job} light />
            </div>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="container-hm px-6 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-7">
            <h2 className="text-2xl font-bold text-slate-900">Sobre a vaga</h2>
            {(() => {
              const lines = (job.description || '').split('\n').map((l) => l.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
              if (lines.length > 1) {
                return (
                  <ul className="mt-3 space-y-2">
                    {lines.map((l, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-5 h-5 text-hm-orange mt-0.5 shrink-0" /> {l}</li>
                    ))}
                  </ul>
                );
              }
              return <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-line">{job.description}</p>;
            })()}
            <h3 className="text-xl font-bold text-slate-900 mt-7">Requisitos</h3>
            <ul className="mt-3 space-y-2">
              {(job.requirements || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-5 h-5 text-hm-orange mt-0.5 shrink-0" /> {r}</li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-7 h-fit sticky top-24">
            <h3 className="text-lg font-bold text-slate-900">Candidate-se a esta vaga</h3>
            <p className="text-sm text-slate-600 mt-1">Envie sua candidatura em poucos cliques e acompanhe o status.</p>
            <div className="mt-5 divide-y divide-slate-100 text-sm">
              <div className="flex items-start justify-between gap-6 py-2"><span className="text-slate-500 shrink-0">Modalidade</span><span className="font-semibold text-slate-900 text-right">{job.modality}</span></div>
              <div className="flex items-start justify-between gap-6 py-2"><span className="text-slate-500 shrink-0">Contrato</span><span className="font-semibold text-slate-900 text-right">{job.type}</span></div>
              {job.openings > 1 && <div className="flex items-start justify-between gap-6 py-2"><span className="text-slate-500 shrink-0">Vagas</span><span className="font-semibold text-slate-900 text-right">{job.openings} posições</span></div>}
              {job.work_schedule && <div className="flex items-start justify-between gap-6 py-2"><span className="text-slate-500 shrink-0">Horário</span><span className="font-semibold text-slate-900 text-right whitespace-pre-line break-words">{job.work_schedule}</span></div>}
              {job.work_scale && <div className="flex items-start justify-between gap-6 py-2"><span className="text-slate-500 shrink-0">Escala</span><span className="font-semibold text-slate-900 text-right">{job.work_scale}</span></div>}
              <div className="flex items-start justify-between gap-6 py-2"><span className="text-slate-500 shrink-0">Salário</span><span className="font-semibold text-slate-900 text-right">{job.salary}</span></div>
              {(job.hazard_pay || job.unhealthy_pay) && <div className="py-2"><JobSalaryExtras job={job} /></div>}
            </div>
            {job.benefits && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm font-bold text-slate-900 mb-1">Benefícios</p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{job.benefits}</p>
              </div>
            )}
            {applied ? (
              <div data-testid="already-applied-badge" className="mt-5 bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Você já se candidatou</div>
            ) : (
              <Button data-testid="apply-job-btn" onClick={applyToJob} disabled={applying} className="w-full mt-5 bg-hm-orange hover:bg-orange-600 rounded-full h-12 text-base">{applying ? 'Enviando...' : 'Candidatar-se agora'}</Button>
            )}
            <button
              data-testid="favorite-job-btn"
              onClick={toggleFavorite}
              className={`w-full mt-3 h-11 rounded-full border font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors ${isFav ? 'bg-red-50 border-red-200 text-red-600' : 'bg-white border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-500'}`}
            >
              <Heart className={`w-4 h-4 ${isFav ? 'fill-red-500' : ''}`} /> {isFav ? 'Salva nos favoritos' : 'Favoritar vaga'}
            </button>
            {!user && (
              <p className="text-xs text-slate-500 mt-3 text-center">Você precisa <Link className="text-hm-orange font-semibold" to="/portal/candidato/login">entrar</Link> ou <Link className="text-hm-orange font-semibold" to="/portal/candidato/cadastro">criar conta</Link>.</p>
            )}
          </div>
        </div>

        {similar.length > 0 && (
          <div className="container-hm px-6 mt-12" data-testid="similar-jobs-section">
            <h2 className="text-2xl font-bold text-slate-900">Você também pode se interessar por</h2>
            <p className="text-sm text-slate-500 mt-1">Vagas relacionadas ao mesmo cargo, área ou segmento.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
              {similar.map((s) => (
                <Link
                  key={s.id}
                  to={`/vaga/${s.id}`}
                  data-testid={`similar-job-${s.id}`}
                  className="bg-white border border-slate-100 rounded-2xl p-5 hover-lift block group"
                >
                  <p className="text-xs font-semibold text-hm-orange uppercase">{s.area}</p>
                  <h3 className="font-bold text-slate-900 mt-0.5 group-hover:text-hm-orange transition-colors">{s.title}</h3>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {s.location}</span>
                    <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {s.type}</span>
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-hm-orange">Ver vaga <ArrowRight className="w-3.5 h-3.5" /></span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default VagaDetailPage;
